import React from "react";
import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import { fighterApi } from "../services/api";
import "../components/events/FighterView.css";

const FIGHTS_PER_PAGE = 7;

const formatRecord = (fighter) => {
    const wins = fighter?.wins ?? 0;
    const losses = fighter?.losses ?? 0;
    const draws = fighter?.draws ?? 0;
    const noContests = fighter?.no_contests ?? 0;

    let record = `${wins}-${losses}-${draws}`;

    if (noContests > 0) {
        record += ` (${noContests} NC)`;
    }

    return record;
};

const formatHeight = (heightInches) => {
    if (typeof heightInches !== "number") {
        return "N/A";
    }

    const feet = Math.floor(heightInches / 12);
    const inches = heightInches % 12;

    return `${feet}'${inches}" (${heightInches} in)`;
};

const formatReach = (reachInches) => {
    if (typeof reachInches !== "number") {
        return "N/A";
    }

    return `${reachInches} in`;
};

const getOpponentData = (fight, fighterId) => {
    const isRedCorner = fight?.red_corner_fighter_id === fighterId;

    if (isRedCorner) {
        return {
            id: fight?.blue_corner_fighter_id,
            name: fight?.blue_corner_fighter_name || "Unknown Opponent",
        };
    }

    return {
        id: fight?.red_corner_fighter_id,
        name: fight?.red_corner_fighter_name || "Unknown Opponent",
    };
};

const getFightResult = (fight, fighterId) => {
    if (!fight?.winner_fighter_name) {
        return "DRAW/NC";
    }

    const isRedCorner = fight?.red_corner_fighter_id === fighterId;
    const fighterName = isRedCorner ? fight?.red_corner_fighter_name : fight?.blue_corner_fighter_name;

    return fight.winner_fighter_name === fighterName ? "WIN" : "LOSS";
};

function FighterView() {
    const { id } = useParams();
    const fighterId = Number(id);

    const [fighter, setFighter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!fighterId) {
            setLoading(false);
            return;
        }

        const fetchFighterData = async () => {
            try {
                setLoading(true);
                const fighterData = await fighterApi.getFighterById(fighterId);

                setFighter(fighterData);
                setError(null);
            }
            catch (err) {
                setError(err.message || String(err));
            }
            finally {
                setLoading(false);
            }
        }

        fetchFighterData();
    }, [fighterId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [fighterId]);

    const recentFights = useMemo(() => fighter?.recent_fights || [], [fighter]);
    const totalPages = Math.max(1, Math.ceil(recentFights.length / FIGHTS_PER_PAGE));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const currentFights = useMemo(() => {
        const startIndex = (currentPage - 1) * FIGHTS_PER_PAGE;
        return recentFights.slice(startIndex, startIndex + FIGHTS_PER_PAGE);
    }, [currentPage, recentFights]);

    const pageNumbers = useMemo(() => {
        if (totalPages <= 4) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        return [1, 2, 3, "...", totalPages];
    }, [totalPages]);

    const promotionLabel = fighter?.promotion_name || "No Promotion";
    const nickname = fighter?.nickname;
    const accolades = Array.isArray(fighter?.accolades) ? fighter.accolades : [];

    if (loading) {
        return (
            <div className="container my-4">
                <div className="text-center">
                    <p>Loading fighter data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container my-4">
                <div className="alert alert-danger" role="alert">
                    <h4>Error Loading Fighter</h4>
                    <p>{error}</p>
                    <button 
                        className="btn btn-primary" 
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!fighter) {
        return (
            <div className="container my-4">
                <div className="alert alert-warning" role="alert">
                    No fighter found for ID {id}.
                </div>
            </div>
        );
    }

    return (
        <div className="container my-4 fighter-view">
            <div className="card fighter-hero-card shadow-sm mb-3">
                <div className="card-body fighter-hero-body">
                    <div className="fighter-header-center">
                        <p className="fighter-promotion-label">{promotionLabel}</p>
                        <div className="fighter-header-main">
                            <h1 className="fighter-name">{fighter.name}</h1>
                            <p className="fighter-nickname">{nickname ? `"${nickname}"` : ""}</p>
                        </div>
                    </div>
                    <span className={`fighter-status-pill ${fighter.active ? "active" : "inactive"}`}>
                        {fighter.active ? "Active" : "Inactive"}
                    </span>
                </div>
            </div>

            <div className="row g-3">
                <div className="col-12 col-lg-6">
                    <div className="card fighter-info-card shadow-sm h-100">
                        <div className="card-body">
                            <h2 className="fighter-section-title">Fighter Accolades</h2>

                            {accolades.length > 0 ? (
                                <ul className="fighter-accolades-list">
                                    {accolades.map((accolade, index) => (
                                        <li key={`${accolade}-${index}`}>{accolade}</li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="fighter-record-row">
                                    <div className="fighter-record-chip">
                                        <span className="chip-label">Record</span>
                                        <span className="chip-value">{formatRecord(fighter)}</span>
                                    </div>
                                    <div className="fighter-record-chip">
                                        <span className="chip-label">Wins</span>
                                        <span className="chip-value">{fighter.wins ?? 0}</span>
                                    </div>
                                    <div className="fighter-record-chip">
                                        <span className="chip-label">Losses</span>
                                        <span className="chip-value">{fighter.losses ?? 0}</span>
                                    </div>
                                </div>
                            )}

                            <div className="fighter-info-divider" />
                            <h3 className="fighter-info-title">Fighter Info</h3>

                            <div className="fighter-info-grid">
                                <p><span>Birthplace:</span> {fighter.place_of_birth || "N/A"}</p>
                                <p><span>Date of Birth:</span> {fighter.date_of_birth_str || "N/A"}</p>
                                <p><span>Height:</span> {formatHeight(fighter.height)}</p>
                                <p><span>Reach:</span> {formatReach(fighter.reach)}</p>
                                <p><span>Gym:</span> {fighter.training_gym || "N/A"}</p>
                                <p><span>Training Location:</span> {fighter.training_location || "N/A"}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-6">
                    <div className="card fighter-fights-card shadow-sm h-100">
                        <div className="card-body fighter-fights-body">
                            <h2 className="fighter-section-title">Fights</h2>

                            <div className="fighter-fight-list">
                                {currentFights.length ? (
                                    currentFights.map((fight, index) => {
                                        const opponent = getOpponentData(fight, fighter.fighter_id);
                                        const result = getFightResult(fight, fighter.fighter_id);

                                        return (
                                            <article className="fighter-fight-row" key={fight.fight_id || index}>
                                                <div className="fighter-fight-row-top">
                                                    <span className={`fight-result ${result.toLowerCase().replace("/", "-")}`}>
                                                        {result}
                                                    </span>
                                                    <span className="fight-event-name">
                                                        {fight.event_name || "Fight Info Goes Here"}
                                                    </span>
                                                </div>

                                                <div className="fighter-fight-row-bottom">
                                                    <span className="fight-opponent">
                                                        vs{" "}
                                                        {opponent.id ? (
                                                            <Link to={`/fighter/${opponent.id}`}>{opponent.name}</Link>
                                                        ) : (
                                                            opponent.name
                                                        )}
                                                    </span>
                                                    <span className="fight-meta">
                                                        {fight.method || "N/A"}
                                                        {fight.round ? ` | R${fight.round}` : ""}
                                                        {fight.time_str ? ` ${fight.time_str}` : ""}
                                                    </span>
                                                </div>
                                            </article>
                                        );
                                    })
                                ) : (
                                    <div className="fighter-no-fights">No recorded fights yet.</div>
                                )}
                            </div>

                            {recentFights.length > 0 && (
                                <nav className="fighter-pagination" aria-label="Fight history pages">
                                    {pageNumbers.map((page, index) => {
                                        if (page === "...") {
                                            return (
                                                <span className="fighter-page-ellipsis" key={`ellipsis-${index}`}>
                                                    ...
                                                </span>
                                            );
                                        }

                                        return (
                                            <button
                                                className={`fighter-page-button ${currentPage === page ? "active" : ""}`}
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                type="button"
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                </nav>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FighterView;
